import React, { useEffect, useState } from 'react';
import { Database, Plus, X, Check, AlertTriangle, RefreshCw, Server, Trash2 } from 'lucide-react';
import { API, apiFetch } from '../lib/api';

const EMPTY = {
  label: '', kind: 'MSSQL', server: '', database: '',
  user: '', password: '', port: 1433, encrypt: true, notes: ''
};

export function SqlCredentialsManager() {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm]         = useState(EMPTY);
  const [editId, setEditId]     = useState(null);
  const [saving, setSaving]     = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [busyId, setBusyId]     = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await apiFetch(`${API}/sql-credentials`);
      const d = await r.json();
      setProfiles(d.profiles || []);
    } catch { setProfiles([]); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const startCreate = () => { setEditId(null); setForm(EMPTY); setShowForm(true); setTestResult(null); };
  const startEdit = (p) => {
    setEditId(p.id);
    setForm({
      label: p.label || '', kind: p.kind || 'MSSQL',
      server: p.server || '', database: p.database || '',
      user: p.user || '', password: '',
      port: p.port || 1433, encrypt: p.encrypt !== false, notes: p.notes || ''
    });
    setShowForm(true);
    setTestResult(null);
  };
  const closeForm = () => { setShowForm(false); setEditId(null); setTestResult(null); };

  const save = async () => {
    if (!form.label || !form.server || !form.database || !form.user) {
      setTestResult({ ok: false, error: 'Label, server, database, and user are required' });
      return;
    }
    setSaving(true);
    try {
      const url    = editId ? `${API}/sql-credentials/${editId}` : `${API}/sql-credentials`;
      const method = editId ? 'PUT' : 'POST';
      const res    = await apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      const data = await res.json();
      if (res.ok && data.success) {
        closeForm();
        await load();
      } else {
        setTestResult({ ok: false, error: data.error || data.message || 'Save failed' });
      }
    } catch (e) { setTestResult({ ok: false, error: e.message }); }
    setSaving(false);
  };

  const removeProfile = async (p) => {
    if (!window.confirm(`Delete profile "${p.label}"?`)) return;
    setBusyId(p.id);
    await apiFetch(`${API}/sql-credentials/${p.id}`, { method: 'DELETE' });
    setBusyId(null);
    await load();
  };

  const activate = async (p) => {
    setBusyId(p.id);
    const r = await apiFetch(`${API}/sql-credentials/${p.id}/activate`, { method: 'POST' });
    const d = await r.json();
    setBusyId(null);
    if (d.success) { alert(d.message || 'Profile activated. Restart backend to apply.'); }
    else { alert(d.message || 'Activation failed.'); }
    await load();
  };

  const test = async (p) => {
    setBusyId(p.id);
    setTestResult(null);
    const r = await apiFetch(`${API}/sql-credentials/${p.id}/test`, { method: 'POST' });
    const d = await r.json();
    setBusyId(null);
    setTestResult({ ok: d.ok, ...d, profileId: p.id });
  };

  return (
    <div className="card" style={{ borderTop: '3px solid #8b5cf6' }} data-testid="sql-credentials-card">
      <div className="card-header">
        <div>
          <div className="card-title">SQL Login Credentials</div>
          <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>
            Manage SQL connection profiles. The active profile drives MSSQL on next backend restart.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-outline" style={{ fontSize: 10 }} onClick={load} data-testid="sql-creds-refresh">
            <RefreshCw size={11} /> Reload
          </button>
          <button className="btn btn-primary" style={{ fontSize: 10 }} onClick={startCreate} data-testid="sql-creds-add">
            <Plus size={11} /> Add Profile
          </button>
        </div>
      </div>

      <div style={{ padding: 16 }}>
        {loading ? (
          <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)', fontSize: 12 }}>Loading profiles...</div>
        ) : profiles.length === 0 ? (
          <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)', fontSize: 12 }}>
            No profiles yet. Click <strong>Add Profile</strong> to create one.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {profiles.map(p => (
              <div key={p.id} data-testid={`sql-creds-row-${p.id}`} style={{
                display: 'grid', gridTemplateColumns: '36px 1fr auto', gap: 12, alignItems: 'center',
                padding: 14, border: `1px solid ${p.isActive ? '#3b82f6' : 'var(--border)'}`,
                background: p.isActive ? 'rgba(59,130,246,0.06)' : 'var(--surface)',
                borderRadius: 10
              }}>
                <div style={{
                  width: 36, height: 36, borderRadius: 8,
                  background: p.isActive ? '#3b82f6' : 'var(--surface-3)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>
                  <Database size={16} color={p.isActive ? '#fff' : 'var(--text-secondary)'} />
                </div>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <span style={{ fontWeight: 800, fontSize: 13, color: 'var(--text-primary)' }}>{p.label}</span>
                    <span className="badge badge-blue">{p.kind || 'MSSQL'}</span>
                    {p.isActive && <span className="badge badge-green">ACTIVE</span>}
                    {p.isReadOnly && <span className="badge badge-amber">.env LOCKED</span>}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
                    <Server size={10} style={{ verticalAlign: 'middle' }} /> {p.user}@{p.server}:{p.port} / <strong>{p.database}</strong>
                    {p.encrypt ? ' · encrypt:on' : ' · encrypt:off'} {p.passwordMask ? ' · pw:' + p.passwordMask : ''}
                  </div>
                  {p.notes && <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 4 }}>{p.notes}</div>}
                </div>
                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  <button className="btn btn-outline" style={{ fontSize: 10, padding: '6px 10px' }} disabled={busyId === p.id} onClick={() => test(p)} data-testid={`sql-creds-test-${p.id}`}>
                    {busyId === p.id ? '...' : 'Test'}
                  </button>
                  {!p.isActive && !p.isReadOnly && (
                    <button className="btn btn-primary" style={{ fontSize: 10, padding: '6px 10px' }} disabled={busyId === p.id} onClick={() => activate(p)} data-testid={`sql-creds-activate-${p.id}`}>
                      Activate
                    </button>
                  )}
                  {!p.isReadOnly && (
                    <>
                      <button className="btn btn-outline" style={{ fontSize: 10, padding: '6px 10px' }} disabled={busyId === p.id} onClick={() => startEdit(p)} data-testid={`sql-creds-edit-${p.id}`}>
                        Edit
                      </button>
                      <button className="btn btn-outline" style={{ fontSize: 10, padding: '6px 10px', color: '#dc2626', borderColor: '#fecaca' }} disabled={busyId === p.id} onClick={() => removeProfile(p)} data-testid={`sql-creds-delete-${p.id}`}>
                        <Trash2 size={11} />
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {testResult && testResult.ok != null && (
          <div className={`alert-banner ${testResult.ok ? 'green' : 'red'}`} style={{ marginTop: 14 }} data-testid="sql-creds-test-result">
            {testResult.ok ? <Check size={14} color="#16a34a" /> : <AlertTriangle size={14} color="#dc2626" />}
            <div style={{ fontSize: 12, color: testResult.ok ? '#166534' : '#991b1b' }}>
              {testResult.ok
                ? <>Connected. server=<strong>{testResult.server}</strong> · db=<strong>{testResult.db}</strong></>
                : <>Connection failed: {testResult.error}</>}
            </div>
          </div>
        )}
      </div>

      {/* Modal */}
      {showForm && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }} onClick={closeForm}>
          <div className="card" style={{ width: 580, padding: 0, maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
            <div className="card-header">
              <span className="card-title">{editId ? 'Edit Profile' : 'New SQL Profile'}</span>
              <button onClick={closeForm} style={{ background: 'none', border: 'none', cursor: 'pointer' }} data-testid="sql-creds-form-close">
                <X size={16} color="#94a3b8" />
              </button>
            </div>
            <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label className="form-label">Profile Label *</label>
                <input className="form-input" value={form.label} onChange={e => f('label', e.target.value)} placeholder="e.g. Staging RC26erp_dev" data-testid="sql-creds-input-label" />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12 }}>
                <div>
                  <label className="form-label">Server / Host *</label>
                  <input className="form-input" value={form.server} onChange={e => f('server', e.target.value)} placeholder="120.138.9.33" data-testid="sql-creds-input-server" />
                </div>
                <div>
                  <label className="form-label">Port</label>
                  <input className="form-input" type="number" value={form.port} onChange={e => f('port', parseInt(e.target.value || '1433', 10))} data-testid="sql-creds-input-port" />
                </div>
              </div>
              <div>
                <label className="form-label">Database *</label>
                <input className="form-input" value={form.database} onChange={e => f('database', e.target.value)} placeholder="RC26MASTERDB" data-testid="sql-creds-input-database" />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label className="form-label">SQL User *</label>
                  <input className="form-input" value={form.user} onChange={e => f('user', e.target.value)} placeholder="sa" data-testid="sql-creds-input-user" />
                </div>
                <div>
                  <label className="form-label">Password {editId ? '(leave blank to keep)' : '*'}</label>
                  <input className="form-input" type="password" value={form.password} onChange={e => f('password', e.target.value)} placeholder="••••••" data-testid="sql-creds-input-password" />
                </div>
              </div>
              <div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--text-secondary)', cursor: 'pointer' }}>
                  <input type="checkbox" checked={form.encrypt} onChange={e => f('encrypt', e.target.checked)} data-testid="sql-creds-input-encrypt" />
                  Use encrypted connection (TLS)
                </label>
              </div>
              <div>
                <label className="form-label">Notes</label>
                <textarea className="form-input" rows={2} value={form.notes} onChange={e => f('notes', e.target.value)} placeholder="Purpose / environment / contact..." data-testid="sql-creds-input-notes" />
              </div>

              {testResult && testResult.ok === false && !testResult.profileId && (
                <div className="alert-banner red">
                  <AlertTriangle size={14} color="#dc2626" style={{ flexShrink: 0 }} />
                  <div style={{ fontSize: 12, color: '#991b1b' }}>{testResult.error}</div>
                </div>
              )}

              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
                <button className="btn btn-outline" onClick={closeForm} data-testid="sql-creds-form-cancel">Cancel</button>
                <button className="btn btn-primary" disabled={saving} onClick={save} data-testid="sql-creds-form-save">
                  {saving ? 'Saving...' : (editId ? 'Update Profile' : 'Create Profile')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SqlCredentialsManager;
